/**
 * Lightweight controller for Tava transformation filters + scroll-snap carousel.
 * No third-party slider. Re-inits when the theme editor reloads the section.
 */
(function () {
  'use strict';

  /**
   * Wires one transformation section: filters, arrows, dots, and quote expanders.
   * @param {HTMLElement} root - Section root with [data-tava-transformation]
   */
  function initTransformation(root) {
    if (!root || root.dataset.tpReady === 'true') return;
    root.dataset.tpReady = 'true';

    var track = root.querySelector('[data-tp-track]');
    var filters = root.querySelectorAll('[data-tp-filter]');
    var prev = root.querySelector('[data-tp-prev]');
    var next = root.querySelector('[data-tp-next]');
    var dotsWrap = root.querySelector('[data-tp-dots]');
    if (!track) return;

    var filterValue = 'all';

    /** Visible (non-filtered) cards in the track. */
    function visibleCards() {
      return Array.prototype.slice.call(track.querySelectorAll('[data-tp-card]')).filter(function (card) {
        return !card.hasAttribute('hidden');
      });
    }

    /** Nearest card to the track's horizontal center. */
    function activeIndex() {
      var cards = visibleCards();
      if (!cards.length) return 0;
      var mid = track.scrollLeft + track.clientWidth / 2;
      var best = 0;
      var bestDist = Infinity;
      cards.forEach(function (card, i) {
        var center = card.offsetLeft + card.offsetWidth / 2;
        var dist = Math.abs(center - mid);
        if (dist < bestDist) {
          bestDist = dist;
          best = i;
        }
      });
      return best;
    }

    /** Scrolls so the chosen card sits in the middle of the track. */
    function goTo(index) {
      var cards = visibleCards();
      if (!cards.length) return;
      var i = Math.max(0, Math.min(index, cards.length - 1));
      var card = cards[i];
      var left = card.offsetLeft - (track.clientWidth - card.offsetWidth) / 2;
      track.scrollTo({ left: Math.max(0, left), behavior: 'smooth' });
    }

    /** Rebuilds pagination dots for the current filter set. */
    function renderDots() {
      if (!dotsWrap) return;
      var cards = visibleCards();
      dotsWrap.innerHTML = '';
      cards.forEach(function (_card, i) {
        var btn = document.createElement('button');
        btn.type = 'button';
        btn.className = 'tava-tp__dot';
        btn.setAttribute('aria-label', 'Go to result ' + (i + 1));
        btn.addEventListener('click', function () {
          goTo(i);
        });
        dotsWrap.appendChild(btn);
      });
      updateChrome();
    }

    /** Syncs arrow disabled state, active dot, and featured card class. */
    function updateChrome() {
      var cards = visibleCards();
      var i = activeIndex();
      if (prev) prev.disabled = i <= 0 || cards.length <= 1;
      if (next) next.disabled = i >= cards.length - 1 || cards.length <= 1;
      if (dotsWrap) {
        Array.prototype.forEach.call(dotsWrap.children, function (dot, di) {
          dot.classList.toggle('is-active', di === i);
        });
      }
      Array.prototype.forEach.call(track.querySelectorAll('[data-tp-card]'), function (card) {
        card.classList.remove('is-active');
      });
      if (cards[i]) cards[i].classList.add('is-active');
    }

    /** Shows cards matching the selected concern without a page reload. */
    function applyFilter(value) {
      filterValue = value;
      var cards = track.querySelectorAll('[data-tp-card]');
      Array.prototype.forEach.call(cards, function (card) {
        var concern = card.getAttribute('data-concern') || 'all';
        var match = value === 'all' || concern === value;
        if (match) {
          card.removeAttribute('hidden');
          card.classList.remove('is-exit');
        } else {
          card.classList.add('is-exit');
          window.setTimeout(function () {
            if (filterValue !== 'all' && (card.getAttribute('data-concern') || 'all') !== filterValue) {
              card.setAttribute('hidden', '');
            }
            card.classList.remove('is-exit');
          }, 180);
        }
      });
      track.scrollTo({ left: 0, behavior: 'smooth' });
      window.setTimeout(renderDots, 200);
    }

    filters.forEach(function (btn) {
      btn.addEventListener('click', function () {
        filters.forEach(function (other) {
          other.classList.remove('is-active');
          other.setAttribute('aria-pressed', 'false');
        });
        btn.classList.add('is-active');
        btn.setAttribute('aria-pressed', 'true');
        applyFilter(btn.getAttribute('data-tp-filter') || 'all');
      });
    });

    if (prev) {
      prev.addEventListener('click', function () {
        goTo(activeIndex() - 1);
      });
    }
    if (next) {
      next.addEventListener('click', function () {
        goTo(activeIndex() + 1);
      });
    }

    track.addEventListener('scroll', function () {
      window.requestAnimationFrame(updateChrome);
    });

    root.addEventListener('keydown', function (event) {
      if (event.key === 'ArrowLeft') {
        event.preventDefault();
        goTo(activeIndex() - 1);
      } else if (event.key === 'ArrowRight') {
        event.preventDefault();
        goTo(activeIndex() + 1);
      }
    });

    // Expands long quotes instead of dumping a wall of text.
    Array.prototype.forEach.call(root.querySelectorAll('[data-tp-more]'), function (btn) {
      btn.addEventListener('click', function () {
        var quote = btn.previousElementSibling;
        if (!quote) return;
        var expanded = quote.classList.toggle('is-clamped') === false;
        btn.textContent = expanded ? 'Read less' : 'Read more';
        btn.setAttribute('aria-expanded', expanded ? 'true' : 'false');
      });
    });

    renderDots();
  }

  function scan() {
    document.querySelectorAll('[data-tava-transformation]').forEach(initTransformation);
  }

  if (document.readyState === 'loading') {
    document.addEventListener('DOMContentLoaded', scan);
  } else {
    scan();
  }

  document.addEventListener('shopify:section:load', function (event) {
    var root = event.target.querySelector('[data-tava-transformation]');
    if (root) {
      root.dataset.tpReady = 'false';
      initTransformation(root);
    }
  });
})();
